//#region Import Required Libraries

import { Util } from "../util";
import { __messageTimestamp } from "./../constant";
import { StorageHelper } from "./../helpers";
import { MessageContent } from "./../types";

//#endregion

//#region TimestampsUtil Class

/**
 * @class TimestampsUtil
 * @classdesc Provides utility methods for managing message timestamps.
 *
 * This class is designed to handle operations such as retrieving, saving,
 * and removing timestamps for messages, ensuring proper tracking and ordering.
 * It is especially useful for applications dealing with time-sensitive messages.
 *
 * @see {@link StorageHelper} for generic storage operations.
 * @see {@link MessageContent} for the expected structure of a message.
 */
export class TimestampsUtil {
	/**
	 * Returns a floating-point number with a specified fixed precision.
	 * If an error occurs or the input is invalid, it returns the original number.
	 *
	 * @param {number} value - The input floating-point number.
	 * @param {number} precision - The number of decimal places to round to. Defaults to **3**.
	 * @returns {number} - The number rounded to the specified precision or the original number on error.
	 */
	static toFixedPrecision(value: number, precision: number = 3): number {
		try {
			if (typeof value !== "number" || isNaN(value)) {
				return value; // Return original value if it's not a valid number
			}

			if (typeof precision !== "number" || isNaN(precision) || precision < 0) {
				return value; // Return original value if precision is invalid
			}

			return parseFloat(value.toFixed(precision));
		} catch (ex) {
			return value;
		}
	}

	/**
	 * SD-5918: Retrieves the last message timestamp from the storage for every message received.
	 *
	 * @param pageId - The unique identifier of the page to get the timestamp for
	 * @returns The timestamp as a number (defaults to 0 if not found or on error)
	 *
	 * @remarks
	 * The timestamp is stored in local storage with key format "swc_msg_ts_[pageId]"
	 */
	static getLastMessageTimestamp(pageId: string) {
		try {
			if (!pageId) return 0;
			const storageKey = __messageTimestamp + pageId;
			const tsStorage = StorageHelper.get(storageKey) || "0";
			const value = parseFloat(tsStorage) || 0;
			return this.toFixedPrecision(value);
		} catch (ex) {
			//console.error('Error retrieving last message timestamp:', ex);
		}
		return 0;
	}

	/**
	 * Retrieves the timestamp of the current message.
	 *
	 * @param {MessageContent} message - The message object containing the timestamp information.
	 * @returns {number} The timestamp of the message as a floating-point number, or 0 if the timestamp is not available.
	 *
	 * @remarks
	 * This function attempts to extract the `messageTimestamp` from the `recordInfo` property of the provided `message` object.
	 * It validates the presence of the `message` and `recordInfo` properties and ensures that the `messageTimestamp` is either a number or a string.
	 * If the `messageTimestamp` is valid, it is parsed to a floating-point number and returned. If any validation fails or an error occurs, the function returns 0.
	 */
	static getCurrentMessageTimestamp(message: MessageContent) {
		try {
			// Validate that message and recordInfo exist
			if (message && message.recordInfo && typeof message.recordInfo.messageTimestamp !== "undefined") {
				const messageTimestamp = message.recordInfo.messageTimestamp || 0;

				// Ensure messageTimestamp is a number or can be converted to a string
				if (typeof messageTimestamp === "number" || typeof messageTimestamp === "string") {
					const value = parseFloat(String(messageTimestamp)) || 0; // Return parsed timestamp or 0
					return this.toFixedPrecision(value);
				}
			}
		} catch (ex) {
			//console.error('Error retrieving current message timestamp:', ex);
		}
		return 0; // Return 0 if validations fail or an error occurs
	}

	/**
	 * Saves the timestamp of the last message for a specific page in the local storage.
	 * @param pageId - The unique identifier of the page.
	 * @param timestamp - The timestamp to be saved (in milliseconds).
	 * @throws {Error} May throw an error during storage operations.
	 * @remarks
	 * The timestamp is stored using a combination of a predefined prefix and the pageId.
	 * Invalid timestamps (null, undefined, or NaN) will cause the function to return without saving.
	 */
	static saveMessageTimestamp(pageId: string, timestamp: number) {
		try {
			if (!pageId) return; // Skip saving if the pageId is empty
			const storageKey = __messageTimestamp + pageId;
			if (Util.isNullOrEmpty(storageKey)) return; // Skip saving if the storage key is empty

			// Validate the timestamp before saving it to the storage.
			if (!timestamp || isNaN(timestamp)) return;
			StorageHelper.set(storageKey, timestamp.toString());
		} catch (ex) {
			//console.error("Error saving message timestamp:", ex);
		}
	}

	/**
	 * Removes the message timestamp from storage for a specific page.
	 * @param pageId - The unique identifier of the page for which to remove the timestamp.
	 */
	static removeMessageTimestamp(pageId: string) {
		try {
			if (!pageId) return; // Skip removing if the pageId is empty
			const storageKey = __messageTimestamp + pageId;
			if (Util.isNullOrEmpty(storageKey)) return; // Skip removing if the storage key is empty

			if (StorageHelper.get(storageKey) === null) return; // Skip removing if the key does not exist
			StorageHelper.remove(storageKey);
		} catch (ex) {
			//console.error("Error removing message timestamp:", ex);
		}
	}
}

//#endregion
